import { useEffect, useRef, useState } from "react";
import { fetchEntityUsers } from "@features/user/api/userApi";
import { EntityUser } from "@features/user/types/user";
import {
    INVITE_USER_SEARCH_DEBOUNCE_MS,
    INVITE_USER_SEARCH_MIN_CHARS,
    INVITE_USER_SEARCH_PER_PAGE,
} from "@features/admin/invitations/constants/inviteModal";
import {
    findExactInviteUserMatch,
    inviteNewUserMatchQuery,
} from "@features/admin/invitations/utils/findExactInviteUserMatch";

export interface InviteNewUserMatchResult {
    matchedUser: EntityUser | null;
    loading: boolean;
}

/**
 * Debounced workplace-directory lookup for the New user tab. Banner-worthy only
 * when email or TRN exactly matches a returned user (same pool as Existing search).
 */
export function useInviteNewUserMatch(
    ownScope: string,
    ownScopeRefId: number,
    enabled: boolean,
    email: string,
    trn: string
): InviteNewUserMatchResult {
    const [matchedUser, setMatchedUser] = useState<EntityUser | null>(null);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!enabled || !(ownScopeRefId > 0) || !ownScope) {
            requestIdRef.current += 1;
            setMatchedUser(null);
            setLoading(false);
            return;
        }

        const query = inviteNewUserMatchQuery(email, trn, INVITE_USER_SEARCH_MIN_CHARS);
        if (!query) {
            requestIdRef.current += 1;
            setMatchedUser(null);
            setLoading(false);
            return;
        }

        const requestId = ++requestIdRef.current;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            try {
                const params: Record<string, string> = {
                    freetext: query,
                    page: "1",
                    perPage: String(INVITE_USER_SEARCH_PER_PAGE),
                };
                if (ownScope === "NATION" || ownScope === "DISTRICT") {
                    params.includeChildren = "true";
                }

                const response = await fetchEntityUsers(ownScope, ownScopeRefId, params);
                if (requestId !== requestIdRef.current) return;

                if (!response.isSuccess()) {
                    setMatchedUser(null);
                    return;
                }

                const rows = response.getData()?.data ?? [];
                setMatchedUser(findExactInviteUserMatch(rows, email, trn));
            } catch {
                if (requestId !== requestIdRef.current) return;
                setMatchedUser(null);
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        }, INVITE_USER_SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [email, trn, ownScope, ownScopeRefId, enabled]);

    return { matchedUser, loading };
}
